import React, { useMemo } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { getCampaignCampaignMessagesByCampaignId } from "redux/selector"
import { updateCampaignCampaignMessages } from "redux/slice/campaign-campaign-messages"
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { styles } from "./styles";
import { getCampaignById } from "redux/selector";
import { Messages } from "./messages";
import { EditCampaign } from "./edit-campaign";
import { EditCampaign as EditCampaignVOld } from "./edit-campaign/v-old";
import { Subscribers } from "./subscribers";
import { NotFoundPage } from "../not-found";
import { CopyID } from "component/shared/copy-id";
import { campaignTriggersEnabled } from "shared/env";
import { useCampaignLink } from "./use-campaign-link";

// The tabs, in order. A `?tab=` link resolves against these labels.
const tabLabels = ["Messages", "Settings", "Subscribers"];


interface Props extends
	PageProps,
	Router.RouteComponentProps<RouteParams>,
	WithStyles<typeof styles>
{
}

interface RouteParams {
	id: string;
}

interface Tab {
	label: string;
	view: React.ReactElement;
}

const Component: React.FC<Props> = ({ user, classes = {}, match, location }) => {
	// MAX-4201: "Go to Settings" links here as `?tab=settings&focus=market`.
	const { tabIndex, setTabIndex, focusMarket, onMarketFocused } = useCampaignLink(location.search, tabLabels);
	const dispatch = useAppThunkDispatch();

	const campaignId = parseInt(match.params.id);
	
	const campaignCampaignMessages = useSelector((state: any) => 
		getCampaignCampaignMessagesByCampaignId(state, campaignId)
	);
	const campaign = useSelector((state: any) => getCampaignById(state, campaignId));

	const tabs: Tab[] = useMemo(() => {
		if (!campaign) return [];
		
		return [
			{
				label: tabLabels[0],
				view: <Messages campaignId={campaign.id} />,
			},
			{
				label: tabLabels[1],
				view: campaignTriggersEnabled
					? <EditCampaign campaign={campaign} focusMarket={focusMarket} onMarketFocused={onMarketFocused} />
					: <EditCampaignVOld campaign={campaign} />,
			},
			{
				label: tabLabels[2],
				view: <Subscribers campaign={campaign} />,
			},
		];
	}, [campaign, focusMarket, onMarketFocused]);

	const title = "Campaign";
	
	if (!campaign) {
		return <NotFoundPage />
	}

	return (
		<DashboardLayout
			permitted={user && user.permissions.campaigns}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Send />
					</FeatherIcon>
					{title}: {campaign.name} <CopyID id={campaign.id} text="Copy Campaign ID" onHeader/>
				</Mui.Typography>
			}
		>
			<Mui.Grid container direction="column" wrap="nowrap" justifyContent="center">
				<Mui.Grid item className={classes.timeline}>
					<Mui.Tabs
						value={tabIndex}
						variant="standard"
						onChange={(event, newTabIndex) => setTabIndex(newTabIndex)}
						centered={false}
						scrollButtons={false}
						textColor="secondary"
					>
						{tabs.map((tab, index) => {
							return (
								<Mui.Tab key={index} label={tab.label}
							/>);
						})}
					</Mui.Tabs>
				</Mui.Grid>
				<Mui.Grid item>
					{tabs[tabIndex].view}
				</Mui.Grid>
			</Mui.Grid>
		</DashboardLayout>
	);
};

export const CampaignPage = withStyles(Component, styles);